// /webinar — Webinar funnel landing page (separate from the main /  landing).
//
// Standalone single-purpose page: one goal — register for the live webinar.
// Mirrors the classic webinar-funnel section order:
//   Hero (date + countdown + CTA)
//   → Opportunity ("Warum du LIVE dabei sein solltest")
//   → Target audience ("Ist dieses Webinar für dich?")
//   → Value reasons ("Das darfst du NICHT verpassen")
//   → About / credibility (Geschichte · Warum jetzt · Versprechen)
//   → Requirements (3 things you need)
//   → Final CTA
//   → Minimal footer
//
// Tracking + consent uses the same patterns as the main App.jsx — the CTA
// click is treated as click-wrap consent for Functional/Statistics/Marketing
// and opens the Calendly popup that doubles as the registration form.
//
// WEBINAR_DATE and WEBINAR_REGISTRATION_URL are the two knobs you tweak
// between webinar runs.

import React from 'react';
import { Icon } from './Icon.jsx';
import { LogoMark } from './LogoMark.jsx';
import { AAConsent } from './CookieBanner.jsx';
import { CookieBanner } from './CookieBanner.jsx';
import { trackCTAClick, trackScheduleIntent } from '../lib/tracking.js';
import { WEBINAR_DATE, WEBINAR_FORMAT, DATE_FMT, TIME_FMT, isWebinarPast, WEBINAR_TBD_LABEL } from '../lib/webinar.js';
import { attachLeadSourceFiller } from '../lib/lead-source.js';
import { attachWebinarLeadIdCapture } from '../lib/webinar-lead-id.js';
import { attachFbTrackingFiller } from '../lib/fb-tracking.js';

// ActiveCampaign embed — form id 7 on the Incibit AC account. The placeholder
// div (`._form_7`) sits inside <WebinarFormModal/>; the script's auto-scan
// renders the form into it on load.
const AC_FORM_SCRIPT_SRC = 'https://incibit62741.activehosted.com/f/embed.php?id=7';
const AC_FORM_DIV_CLASS = '_form_7';

// AC hidden field that receives the captured lead source (utm_source / li /
// full query string). Form 7 declares this field via `window.cfields = {"2":"leadsource"}`,
// so the hidden input's name is `field[2]`.
const AC_LEAD_SOURCE_FIELD = 'field[2]';

// AC hidden field that receives the Meta CAPI tracking JSON (fbc/fbp/ua/url).
// Form 7 declares it via `window.cfields = {"3":"tracking"}`, so the hidden
// input's name is `field[3]`. The AC→Meta CAPI function (functions/index.js)
// parses this JSON and forwards the signals into user_data so server-side
// Leads stay attributable even when the browser Pixel is blocked.
const AC_TRACKING_FIELD = 'field[3]';

// ─── CTA HANDLER ───────────────────────────────────────────────────────────
// CTAs across the page dispatch this event; the page-level WebinarFormModal
// listens for it and flips its `open` state. We avoid prop-drilling a setter
// through every section component this way.
const OPEN_EVENT = 'aa:open-webinar-modal';

function openWebinarRegistration(location) {
  // Click-wrap consent — opening the registration form implies opt-in for
  // Functional (AC + form-tracking cookies), Statistics (GA4) and Marketing
  // (Meta / Google Ads). Set BEFORE the track*() calls so the pixel doesn't
  // drop them at its consent gate.
  const c = AAConsent.current();
  if (!c.functional || !c.statistics || !c.marketing) {
    AAConsent.set({ functional: true, statistics: true, marketing: true });
  }
  trackCTAClick({ label: 'Webinar-Anmeldung', location, destination: 'activecampaign' });
  trackScheduleIntent({ location });
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { location } }));
}

function useCountdown(target) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, isPast: diff === 0 };
}

// ─── HEAD ──────────────────────────────────────────────────────────────────
// Override title + description for the webinar route. The main index.html
// metas are for /, so we set them imperatively when this page mounts.
const WEBINAR_CANONICAL = 'https://app-academy.ai/webinar';

function useWebinarHead() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Kostenloses Webinar der AI App Academy. In 6 klaren Schritten zur eigenen App.';
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta) {
      meta.setAttribute(
        'content',
        'Kostenloses Live-Webinar der AI App Academy. Wie du mit KI in 6 klaren Schritten deine eigene App baust und im Store launchst. Ohne Programmierkenntnisse. Ohne teure Agentur. Jetzt Platz sichern.'
      );
    }

    // Canonical + og:url: the index.html defaults point at "/", so on this route
    // we repoint them to the webinar URL. Without this, /webinar declares the
    // homepage as its canonical and search engines drop the webinar page.
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (canonical) canonical.setAttribute('href', WEBINAR_CANONICAL);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const prevOgUrl = ogUrl ? ogUrl.getAttribute('content') : null;
    if (ogUrl) ogUrl.setAttribute('content', WEBINAR_CANONICAL);

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute('content', prevDesc);
      if (canonical && prevCanonical != null) canonical.setAttribute('href', prevCanonical);
      if (ogUrl && prevOgUrl != null) ogUrl.setAttribute('content', prevOgUrl);
    };
  }, []);
}

// ═══════════════════════════════════════════════════════════════════════════
//  COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function WebinarNav() {
  return (
    <nav className="nav nav-webinar">
      <div className="nav-inner">
        <a href="#top" className="logo">
          <div className="logo-mark"><LogoMark size={32} /></div>
          <span className="logo-text">AI App Academy</span>
        </a>
        <a
          href="#anmeldung"
          onClick={(e) => { e.preventDefault(); openWebinarRegistration('nav'); }}
          className="btn btn-primary btn-sm nav-cta"
        >
          <span className="cta-label-full">Jetzt Platz sichern</span>
          <span className="cta-label-short">Platz sichern</span>
          <span className="btn-arrow"><Icon.ArrowRight size={14} /></span>
        </a>
      </div>
    </nav>
  );
}

function CountdownBox({ value, label }) {
  return (
    <div className="countdown-box">
      <div className="countdown-num">{String(value).padStart(2, '0')}</div>
      <div className="countdown-label">{label}</div>
    </div>
  );
}

function WebinarHero() {
  const { days, hours, minutes, seconds, isPast } = useCountdown(WEBINAR_DATE);

  return (
    <section className="hero webinar-hero" id="top">
      <div className="hero-bg-grid" aria-hidden="true"></div>
      <div className="container">
        <div className="webinar-eyebrow">
          <span className="webinar-live-dot"></span>
          {/* Already uppercase + capital ẞ so text-transform doesn't downgrade
              the name. The host suffix hides on mobile to keep the pill on one
              line; the name still shows up in subline + about, so no loss. */}
          <span style={{ textTransform: 'none' }}>
            LIVE-WEBINAR FÜR SELBSTSTÄNDIGE, GRÜNDER &amp; EXPERTEN
          </span>
        </div>

        <h1 className="hero-headline webinar-headline">
          Wie du dir mit <span className="accent">KI</span> dein eigenes <span className="accent">App-Business</span> baust.<br />
          Eine App im Store, die Umsatz macht, ohne Code, ohne Entwicklerteam.
        </h1>

        <p className="webinar-sub">
          Oft sind die simpelsten Apps die umsatzstärksten: die Apps unserer Kunden machen{' '}
          <strong>1.000 bis 3.000 € und mehr</strong> im Monat. Im Live-Webinar zeige ich dir,
          wie aus deiner Idee in <strong>6 klaren Schritten</strong> ein verkaufbares Produkt
          wird, statt weiter Zeit gegen Geld zu tauschen.
        </p>

        <div className="hero-microproof">
          <span className="hero-microproof-item"><Icon.CheckCircle size={14} /> 30+ Apps live im Store</span>
          <span className="hero-microproof-dot"></span>
          <span className="hero-microproof-item"><Icon.Download size={14} /> 1 Mio.+ Downloads weltweit</span>
        </div>

        <div className="event-meta-pill">
          {isPast ? (
            <span className="event-meta-item">
              <Icon.Calendar size={16} />
              <span>{WEBINAR_TBD_LABEL}</span>
            </span>
          ) : (
            <>
              <span className="event-meta-item">
                <Icon.Calendar size={16} />
                <span>{DATE_FMT.format(WEBINAR_DATE)}</span>
              </span>
              <span className="event-meta-divider"></span>
              <span className="event-meta-item">
                <Icon.Clock size={16} />
                <span>{TIME_FMT.format(WEBINAR_DATE)} Uhr</span>
              </span>
            </>
          )}
        </div>

        {!isPast && (
          <div className="countdown" aria-label="Countdown bis zum Webinar">
            <CountdownBox value={days} label="Tage" />
            <span className="countdown-sep">:</span>
            <CountdownBox value={hours} label="Stunden" />
            <span className="countdown-sep">:</span>
            <CountdownBox value={minutes} label="Minuten" />
            <span className="countdown-sep">:</span>
            <CountdownBox value={seconds} label="Sekunden" />
          </div>
        )}

        <div className="hero-cta-row webinar-cta-row">
          <a
            href="#anmeldung"
            onClick={(e) => { e.preventDefault(); openWebinarRegistration('hero'); }}
            className="btn btn-primary btn-xl"
          >
            <span className="btn-label-stack">
              <span className="btn-main">Jetzt kostenlosen Platz sichern</span>
              <span className="btn-sub">Live mit Dominik · Auf 100 Plätze begrenzt · Q&amp;A zu deinem Vorhaben</span>
            </span>
            <span className="btn-arrow"><Icon.ArrowRight size={18} /></span>
          </a>
        </div>
      </div>
    </section>
  );
}

function WebinarOpportunity() {
  return (
    <section className="section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Warum jetzt</span>
          <h2 className="section-title">Warum aus deiner Idee heute zum ersten Mal ein <span className="serif-italic">verkaufbares Produkt</span> werden kann.</h2>
          <p className="lead">
            Was früher ein Entwicklerteam, 50.000 bis 100.000 € Agenturbudget und Monate an
            Projektzeit gebraucht hat, lässt sich heute mit KI in 6 klaren Schritten umsetzen. Genau das
            macht ein eigenes, verkaufbares Produkt plötzlich auch für Selbstständige, Coaches
            und Gründer realistisch. Kein weiteres Projekt, das deine Zeit frisst, sondern ein
            Asset, das wiederkehrenden Umsatz macht, während dein Hauptgeschäft weiterläuft.
          </p>
          <p className="lead">
            Dazu kommt: Wer jetzt lernt, mit KI eigene Produkte zu bauen, baut sich einen Vorsprung
            auf, den andere erst in 12 bis 24 Monaten aufholen. Der beste Zeitpunkt anzufangen ist,
            bevor es alle tun.
          </p>
        </div>

        <div className="webinar-agenda">
          <div className="webinar-agenda-head">
            <span className="webinar-agenda-eyebrow">Das nimmst du mit</span>
            <h3 className="webinar-agenda-title">Was du im Live-Webinar konkret lernst</h3>
          </div>
          <ul className="webinar-agenda-list">
            <li>
              <Icon.Check size={16} />
              <span>Welche App-Modelle heute wirklich Umsatz machen, von drei- bis fünfstellig pro Monat, und was sie gemeinsam haben.</span>
            </li>
            <li>
              <Icon.Check size={16} />
              <span>Wie aus deiner Idee in 6 klaren Schritten ein fertiges App-Produkt wird, ganz ohne programmieren zu lernen.</span>
            </li>
            <li>
              <Icon.Check size={16} />
              <span>Warum die umsatzstärksten Apps oft bewusst simpel sind, und wie du deine Idee genau darauf zuschneidest.</span>
            </li>
            <li>
              <Icon.Check size={16} />
              <span>Welche Wege es gibt, mit deiner App Umsatz zu machen: Abo, In-App-Kauf, Einmalkauf oder B2B-Lizenz.</span>
            </li>
            <li>
              <Icon.Check size={16} />
              <span>Die häufigsten Fehler am Anfang, und wie du sie von vornherein vermeidest.</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function WebinarAudience() {
  const personas = [
    {
      tag: 'Selbstständige & Business-Builder',
      headline: 'Dein zweites Standbein, das ohne dich Umsatz macht.',
      bullets: [
        'Du willst ein skalierbares Standbein aufbauen, statt weiter Zeit gegen Geld zu tauschen',
        'Du willst ein eigenes Produkt im Store besitzen, das Umsatz macht, während dein Hauptgeschäft weiterläuft',
      ],
    },
    {
      tag: 'Ideengeber & angehende Gründer',
      headline: 'Aus deiner Idee wird ein Produkt mit echtem Umsatz, dein zweites Standbein.',
      bullets: [
        'Du hast eine konkrete App-Idee und willst daraus ein Produkt bauen, das echten Umsatz macht',
        'Du willst einen realistischen Weg ohne Entwicklerteam und ohne sechsstelliges Budget, bist aber bereit, ernsthaft anzufangen',
      ],
    },
  ];
  return (
    <section className="section forwho-section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Für wen es gemacht ist</span>
          <h2 className="section-title">Ist dieses Webinar <span className="serif-italic">für dich</span>?</h2>
        </div>
        <div className="webinar-audience-grid">
          {personas.map((p, i) => (
            <div className="webinar-audience-card" key={i}>
              <div className="webinar-audience-tag">{p.tag}</div>
              <h3 className="webinar-audience-headline">{p.headline}</h3>
              <ul className="webinar-audience-bullets">
                {p.bullets.map((b, j) => (
                  <li key={j}><Icon.Check size={14} /> <span>{b}</span></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Slim full-width boundary line under the two cards — a disqualifier,
            deliberately NOT a third equal card/offer. */}
        <div className="webinar-disqualifier-band">
          <p>
            <strong>Und für wen ist es nichts?</strong> Wenn du passives Einkommen ohne Arbeit
            suchst, einen schnellen Nebenverdienst ohne Investition, oder erstmal nur unverbindlich
            schauen willst, ohne je wirklich zu bauen: Dann spar dir die Zeit. Wir bauen echte
            Produkte für echte Märkte, und das braucht Einsatz, Zeit und die Bereitschaft, in dich
            selbst zu investieren.
          </p>
        </div>
      </div>
    </section>
  );
}

// Slim mid-page CTA banner. Repeats the registration prompt with the event
// date next to the button so visitors reading deep into the page don't have
// to scroll back up or jump to the top-right sticky CTA to convert.
function InlineCTABanner({ location, headline = 'Bereit für deine eigene App?' }) {
  return (
    <section className="inline-cta-banner">
      <div className="container">
        <div className="inline-cta-card">
          <div className="inline-cta-text">
            <div className="inline-cta-headline">{headline}</div>
            <div className="inline-cta-meta">
              <Icon.Calendar size={14} />
              <span>{isWebinarPast() ? WEBINAR_TBD_LABEL : `${DATE_FMT.format(WEBINAR_DATE)} · ${TIME_FMT.format(WEBINAR_DATE)} Uhr · ${WEBINAR_FORMAT}`}</span>
            </div>
          </div>
          <a
            href="#anmeldung"
            onClick={(e) => { e.preventDefault(); openWebinarRegistration(location); }}
            className="btn btn-primary inline-cta-btn"
          >
            <span>Jetzt Platz sichern</span>
            <span className="btn-arrow"><Icon.ArrowRight size={14} /></span>
          </a>
        </div>
      </div>
    </section>
  );
}

// Store-availability badges — self-contained inline SVG logos (Apple + Google
// Play) so no external badge assets are needed and they inherit the dark theme.
// Concrete "you'll find these apps in the real stores" credibility signal.
function StoreBadges() {
  return (
    <div className="webinar-store-badges" aria-label="Verfügbar im Apple App Store und bei Google Play">
      <span className="webinar-store-badge">
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 8.62 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.09 4.09l-.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
        <span className="webinar-store-badge-text">
          <span className="webinar-store-badge-sm">Laden im</span>
          <span className="webinar-store-badge-lg">App Store</span>
        </span>
      </span>
      <span className="webinar-store-badge">
        <svg width="18" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
          <path d="M4.2 2.3a1 1 0 0 0-.5.9v17.6a1 1 0 0 0 .5.9l9.5-9.7L4.2 2.3zm11 8.3L6.5 2.1l10.8 6.2-2.1 2.3zm2.9 1.4 2.7 1.5c.8.5.8 1.6 0 2l-2.7 1.6-2.4-2.6 2.4-2.5zM6.5 21.9l8.7-8.5 2.1 2.3-10.8 6.2z" />
        </svg>
        <span className="webinar-store-badge-text">
          <span className="webinar-store-badge-sm">Jetzt bei</span>
          <span className="webinar-store-badge-lg">Google Play</span>
        </span>
      </span>
    </div>
  );
}

// Visual proof break — one of the apps the team has built, presented as a
// device mockup so the "30+ Apps live im Store" claim becomes concrete.
function WebinarAppShowcase() {
  return (
    <section className="section webinar-showcase-section">
      <div className="container">
        <div className="webinar-showcase-grid">
          <div className="webinar-showcase-text">
            <span className="eyebrow">Echte Apps. Echte Stores.</span>
            <h2 className="webinar-showcase-title">
              30+ Apps live im Store. <span className="serif-italic">Gebaut als Geschäftsmodelle</span>, nicht als Tech-Demos.
            </h2>
            <p>
              Vom Team hinter der Academy gebaut. Von Lern-Apps über digitale Produkte bis zu
              Branded-Apps für Unternehmen, jede davon als eigenständiges Produkt mit eigener Marke
              und realem Monatsumsatz. Entscheidend ist nicht, dass eine App technisch möglich ist.
              Entscheidend ist, dass sie geschäftlich Sinn macht. Genau darauf ist dieses Webinar
              ausgerichtet.
            </p>
            <ul className="webinar-showcase-stats">
              <li>
                <span className="webinar-showcase-stat-num">30+</span>
                <span className="webinar-showcase-stat-lbl">Apps im Store</span>
              </li>
              <li>
                <span className="webinar-showcase-stat-num">1 Mio.+</span>
                <span className="webinar-showcase-stat-lbl">Downloads weltweit</span>
              </li>
              <li>
                <span className="webinar-showcase-stat-num">15+</span>
                <span className="webinar-showcase-stat-lbl">Jahre App-Studio</span>
              </li>
            </ul>
            <StoreBadges />
          </div>
          <div className="webinar-showcase-visual">
            <img
              src="/assets/lernapp%20mockups_klein.png"
              alt="Beispiel-App auf drei Smartphones: Lerneinheiten, Fortschritt und Profil"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function WebinarObjections() {
  const items = [
    {
      objection: 'Ich bin nicht technisch und habe kein Team.',
      response: 'Beides brauchst du nicht. Entscheidend ist ein klares Problem, ein sinnvoller Use Case und der richtige Prozess. Die Umsetzung machst du mit KI selbst, ohne Entwickler einzustellen, zu schulen oder zu steuern.',
    },
    {
      objection: 'Verkauft sich so eine App am Ende überhaupt?',
      response: 'Genau darum geht es. Viele bewusst einfache Apps machen heute Millionenumsätze, oft mit einer einzigen klaren Funktion. Entscheidend ist nicht Komplexität, sondern ein Produkt mit echtem Markt. Im Webinar siehst du an konkreten Beispielen, welche App-Modelle Umsatz machen und welche nicht.',
    },
    {
      objection: 'Ist das nicht extrem teuer?',
      response: 'Die Begleitung in der Academy liegt im vierstelligen Bereich, ein Bruchteil der 30.000 bis 50.000 €, die eine Agentur für dieselbe App aufruft. Du baust dir ein eigenes Asset auf, statt es teuer einzukaufen. Die konkrete Zahl bekommst du transparent im Webinar.',
    },
    {
      objection: 'Wofür kann eine App in meinem Business überhaupt sinnvoll sein?',
      response: 'Du lernst, wann eine App ein echter Hebel für Kundenbindung, Monetarisierung und Skalierung ist. Und wann nicht. Anhand konkreter Modelle aus der Praxis statt aus dem Lehrbuch.',
    },
  ];
  return (
    <section className="section webinar-objections-section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Häufige Zweifel</span>
          <h2 className="section-title">Vielleicht denkst du <span className="serif-italic">gerade</span> …</h2>
        </div>
        <div className="webinar-objections-grid">
          {items.map((it, i) => (
            <div className="webinar-objection-card" key={i}>
              <div className="webinar-objection-quote">„{it.objection}"</div>
              <p className="webinar-objection-response">{it.response}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Subtle social-proof strip — three verbatim customer quotes from the
// Incibit Studio's client portfolio. Quotes are unchanged sentences from
// the main-site testimonials. Section is framed as "Stimmen aus dem Studio"
// so it's transparent that these are Incibit clients (where the team built
// the app), not Academy alumni — Trust-Signal über das Studio, das hinter
// der Academy steht.
// Ascending "revenue ladder" glyph — three bars, filled up to `level`
// (1 = three-figure, 2 = four-figure, 3 = five-figure). Across the three
// cards it reads as a climb (3 → 4 → 5 figures), which is itself the proof:
// results vary and scale upward. Gold, so the money metric pops without
// clashing with the green brand accent.
function RevenueLadder({ level }) {
  const heights = [7, 11, 15];
  return (
    <svg className="webinar-rev-ladder" width="26" height="16" viewBox="0 0 26 16" aria-hidden="true">
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * 9}
          y={16 - h}
          width="6"
          height={h}
          rx="1.5"
          className={i < level ? 'is-on' : 'is-off'}
        />
      ))}
    </svg>
  );
}

function WebinarTestimonials() {
  // `tier` is the honest order-of-magnitude of each client's monthly revenue —
  // exact figures stay private (real client data), the discretion is stated in
  // the section subline and turns the vagueness into a trust signal.
  const items = [
    {
      quote: 'Schon im ersten Monat nach dem Launch meiner neuen App hatte ich über 1.000 Downloads und die ersten wiederkehrenden Abo-Umsätze.',
      name: 'Thomas Kriebernegg',
      role: 'DrawBuddy · Zeichen-App für Kinder',
      photo: '/assets/thomas-kriebernegg.jpeg',
      tier: 'Dreistelliger Monatsumsatz',
      tierNote: 'schon 6 Wochen nach Launch',
      level: 1,
    },
    {
      quote: 'Ich habe nun eine App, die genau meinen Vorstellungen entspricht und großen Mehrwert für Nutzer bietet.',
      name: 'Daniel Bruck',
      role: 'Uniprep · Lern-App für Studenten',
      photo: '/assets/daniel-bruck.jpg',
      tier: 'Vierstelliger Monatsumsatz',
      level: 2,
    },
    {
      quote: 'Wer einen verlässlichen Partner mit hervorragender Ergebnisqualität sucht, ist bei Dominik und seinem Team bestens aufgehoben.',
      name: 'Milan Wolfs',
      role: 'Gardify · App rund um Gartenpflege',
      photo: '/assets/milan-wolfs.jpg',
      tier: 'Fünfstelliger Monatsumsatz',
      level: 3,
    },
  ];
  return (
    <section className="section webinar-testimonials-section">
      <div className="container">
        <div className="webinar-testimonials-eyebrow">Echte Apps. Echte Umsätze.</div>
        <p className="webinar-testimonials-note">
          Alle drei Apps machen Monat für Monat Umsatz. Aus Respekt vor den Zahlen unserer Kunden
          nennen wir die Größenordnung, nicht den exakten Betrag.
        </p>
        <ul className="webinar-testimonials-row">
          {items.map((t, i) => (
            <li className="webinar-testimonial-item" key={i}>
              <div className="webinar-rev-chip">
                <RevenueLadder level={t.level} />
                <span className="webinar-rev-chip-text">
                  <span className="webinar-rev-chip-tier">{t.tier}</span>
                  {t.tierNote && <span className="webinar-rev-chip-note">{t.tierNote}</span>}
                </span>
              </div>
              <p className="webinar-testimonial-quote">„{t.quote}"</p>
              <div className="webinar-testimonial-author">
                <div className="webinar-testimonial-photo">
                  <img src={t.photo} alt="" loading="lazy" decoding="async" width="44" height="44" />
                </div>
                <div className="webinar-testimonial-meta">
                  <span className="webinar-testimonial-name">{t.name}</span>
                  <span className="webinar-testimonial-role">{t.role}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function WebinarAbout() {
  return (
    <section className="section webinar-about-section">
      <div className="container">
        <div className="webinar-about-hero">
          <div className="webinar-about-photo">
            <img
              src="/assets/shooting_chris_joergler_dominik_small.jpg"
              alt="Dominik Groß, Gründer der AI App Academy"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="webinar-about-content">
            <span className="eyebrow">Wer dich begleitet</span>
            <h2 className="section-title">Seit 15 Jahren bin ich der, den man <span className="serif-italic">anruft</span>, wenn man eine App braucht.</h2>
            <p className="lead">
              Unternehmen, Coaches und Startups kommen mit einer Idee zu meinem App-Studio Incibit.
              Sie beschreiben, was sie brauchen, mein Team baut es, und dafür bezahlen sie zwischen
              {' '}<strong>50.000 und 100.000 €</strong>. So sind über 30 Apps mit mehr als 1 Million
              Downloads entstanden.
            </p>
            <p className="lead">
              Mit KI hat sich zum ersten Mal etwas Grundlegendes geändert: Der Engpass, für den man
              uns all die Jahre gebucht hat, lässt sich heute selbst auflösen. Nicht mit einem Klick,
              aber mit dem richtigen Prozess. Genau den zeige ich dir im Webinar: dieselbe
              Vorgehensweise, mit der wir sechsstellige Projekte umsetzen, übersetzt in 6 Schritte,
              die du selbst gehen kannst.
            </p>
            <p className="lead webinar-about-punch">
              Du brauchst kein Entwicklerteam mehr. Du brauchst den Prozess.
            </p>
            <ul className="webinar-about-bullets">
              <li><Icon.Check size={16} /> 15+ Jahre App-Entwicklung im eigenen Studio</li>
              <li><Icon.Check size={16} /> 30+ Apps live im Apple App Store und bei Google Play</li>
              <li><Icon.Check size={16} /> Über 1 Mio. Downloads weltweit</li>
              <li><Icon.Check size={16} /> Du bekommst keinen Technik-Vortrag, sondern einen praxisnahen Einblick, wie aus Expertise, Zielgruppe und Use Case ein verkaufbares App-Produkt wird</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function WebinarRequirements() {
  const items = [
    {
      num: '01',
      text: 'Du hast eine App-Idee, ein bestehendes Angebot oder ein konkretes Thema, das du in eine App überführen willst.',
    },
    {
      num: '02',
      text: 'Du willst verstehen, wie so ein Projekt heute realistisch umsetzbar wird.',
    },
    {
      num: '03',
      text: 'Du bist offen dafür, KI als Hebel statt klassischer Agenturentwicklung zu nutzen.',
    },
  ];
  return (
    <section className="section webinar-requirements-section">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Damit es für dich passt</span>
          <h2 className="section-title">Was dir hilft, damit das Webinar für dich <span className="serif-italic">besonders relevant</span> ist.</h2>
          <p className="lead">
            Du brauchst keine Programmierkenntnisse und kein Entwicklerteam. Was du wirklich
            mitbringen solltest, sind diese 3 Dinge:
          </p>
        </div>
        <div className="webinar-requirements-grid">
          {items.map((it, i) => (
            <div className="webinar-req-card" key={i}>
              <div className="webinar-req-num">{it.num}</div>
              <p>{it.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WebinarFinalCTA() {
  const { days, hours, minutes, seconds, isPast } = useCountdown(WEBINAR_DATE);

  return (
    <section className="cta-final webinar-final-cta" id="anmeldung">
      <div className="container">
        <div className="cta-final-content">
          <div className="webinar-final-logo">
            <LogoMark size={56} />
          </div>
          <span className="eyebrow" style={{ marginBottom: 24, display: 'inline-flex', justifyContent: 'center' }}>
            Dein nächster Schritt
          </span>
          <h2>
            Sichere dir deinen Platz im <em className="serif-italic">Live-Webinar</em>.
          </h2>
          <p>
            Auf 100 Plätze begrenzt, damit am Ende echte Q&amp;A zu deinem konkreten Vorhaben
            möglich ist.{' '}
            {isWebinarPast()
              ? WEBINAR_TBD_LABEL + '.'
              : `Sei live dabei am ${DATE_FMT.format(WEBINAR_DATE)} um ${TIME_FMT.format(WEBINAR_DATE)} Uhr.`}
          </p>

          {!isPast && (
            <div className="countdown countdown-compact" aria-label="Countdown bis zum Webinar">
              <CountdownBox value={days} label="Tage" />
              <span className="countdown-sep">:</span>
              <CountdownBox value={hours} label="Stunden" />
              <span className="countdown-sep">:</span>
              <CountdownBox value={minutes} label="Minuten" />
              <span className="countdown-sep">:</span>
              <CountdownBox value={seconds} label="Sekunden" />
            </div>
          )}

          <a
            href="#"
            onClick={(e) => { e.preventDefault(); openWebinarRegistration('final'); }}
            className="btn btn-primary btn-xl"
          >
            <span className="btn-label-stack">
              <span className="btn-main">Jetzt Platz sichern</span>
              <span className="btn-sub">Live mit Dominik · Auf 100 Plätze begrenzt · Q&amp;A zu deinem Vorhaben</span>
            </span>
            <span className="btn-arrow"><Icon.ArrowRight size={18} /></span>
          </a>

          <div className="cta-meta">
            <span><Icon.CheckCircle size={14} /> Live mit Dominik</span>
            <span><Icon.CheckCircle size={14} /> Auf 100 Plätze begrenzt</span>
            <span><Icon.CheckCircle size={14} /> Q&amp;A zu deinem Vorhaben</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function WebinarFooter() {
  return (
    <footer className="webinar-footer">
      <div className="container">
        <div className="webinar-footer-row">
          <div className="webinar-footer-brand">
            <div className="logo-mark"><LogoMark size={24} /></div>
            <span>© 2026 Incibit GmbH · App Academy</span>
          </div>
          <div className="webinar-footer-links">
            <a href="https://incibit.dev/impressum/" target="_blank" rel="noreferrer">Impressum</a>
            <a href="https://incibit.dev/privacy-policy/" target="_blank" rel="noreferrer">Datenschutz</a>
            <a href="https://incibit.dev/agb/" target="_blank" rel="noreferrer">AGB</a>
            <a
              href="#cookie-settings"
              onClick={(e) => { e.preventDefault(); AAConsent.openSettings(); }}
            >
              Cookie-Einstellungen
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── REGISTRATION MODAL ────────────────────────────────────────────────────
// ActiveCampaign embed wrapped in a Cookie-Banner-style dialog. The form
// placeholder div is mounted ONCE on first page render (not gated on `open`)
// and the AC embed script is injected straight after; that way the form is
// fully hydrated by the time the user clicks a CTA, so the modal feels
// instant on open. Subsequent opens just toggle visibility — the form keeps
// its internal state (any text the user typed) across close/reopen cycles.
function WebinarFormModal({ open, onClose }) {
  const scriptInjectedRef = React.useRef(false);
  const formContainerRef = React.useRef(null);

  // Inject the AC embed script exactly once. requestAnimationFrame guarantees
  // the placeholder div is committed to the real DOM before the script runs
  // — AC's auto-scan only finds divs that exist at script-execution time.
  React.useEffect(() => {
    if (scriptInjectedRef.current) return;
    scriptInjectedRef.current = true;
    requestAnimationFrame(() => {
      const s = document.createElement('script');
      s.src = AC_FORM_SCRIPT_SRC;
      s.async = true;
      s.charset = 'utf-8';
      document.body.appendChild(s);
    });
  }, []);

  // Watch the AC form container and fill the lead-source hidden field as soon
  // as the AC script renders the form. The observer self-disconnects on success
  // or after a 10-second safety timeout (see attachLeadSourceFiller).
  React.useEffect(() => {
    if (!formContainerRef.current) return;
    return attachLeadSourceFiller(formContainerRef.current, AC_LEAD_SOURCE_FIELD);
  }, []);

  // Fill the Meta CAPI tracking field (fbc/fbp/ua/url) as soon as the AC form
  // renders, and refresh it on submit to catch cookies the Pixel set later.
  React.useEffect(() => {
    if (!formContainerRef.current) return;
    return attachFbTrackingFiller(formContainerRef.current, AC_TRACKING_FIELD);
  }, []);

  // Attach a submit-time email-hash capture so the /webinar-thank-you page
  // and the server-side CAPI function derive the same event_id — Meta then
  // explicitly deduplicates the browser Pixel + server CAPI Lead into one
  // conversion count.
  React.useEffect(() => {
    if (!formContainerRef.current) return;
    return attachWebinarLeadIdCapture(formContainerRef.current);
  }, []);

  // Lock body scroll + wire Escape while open.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <div
      className={`webinar-modal-backdrop ${open ? 'is-open' : ''}`}
      aria-hidden={!open}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="webinar-modal" role="dialog" aria-modal="true" aria-labelledby="webinar-modal-title">
        <button
          type="button"
          className="webinar-modal-close"
          onClick={onClose}
          aria-label="Schließen"
        >
          <Icon.X size={20} />
        </button>
        <div className="webinar-modal-header">
          <div className="webinar-modal-eyebrow">Kostenlose Webinar-Anmeldung</div>
          <h2 id="webinar-modal-title">Sichere dir jetzt deinen Platz</h2>
          <p className="webinar-modal-meta">
            {isWebinarPast() ? WEBINAR_TBD_LABEL : `${DATE_FMT.format(WEBINAR_DATE)} · ${TIME_FMT.format(WEBINAR_DATE)} Uhr · ${WEBINAR_FORMAT}`}
          </p>
        </div>
        {/* ActiveCampaign target div — the AC embed script renders the form here. */}
        <div ref={formContainerRef} className={AC_FORM_DIV_CLASS}></div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  PAGE
// ═══════════════════════════════════════════════════════════════════════════

export default function WebinarPage() {
  useWebinarHead();
  const [modalOpen, setModalOpen] = React.useState(false);

  // Bus: every CTA dispatches OPEN_EVENT, the page-level state flips here.
  React.useEffect(() => {
    const onOpen = () => setModalOpen(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Reveal-on-scroll — mirrors the behaviour in App.jsx so sections fade in.
  React.useEffect(() => {
    const sections = document.querySelectorAll('section');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in'); });
    }, { threshold: 0, rootMargin: '0px 0px -8% 0px' });
    sections.forEach((s) => {
      s.classList.add('reveal-on-scroll');
      const rect = s.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) s.classList.add('in');
      io.observe(s);
    });
    const safety = setTimeout(() => {
      document.querySelectorAll('section.reveal-on-scroll:not(.in)').forEach((s) => {
        const rect = s.getBoundingClientRect();
        if (rect.top < window.innerHeight * 1.5) s.classList.add('in');
      });
    }, 1000);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, []);

  return (
    <>
      <WebinarNav />
      <WebinarHero />
      <WebinarOpportunity />
      <WebinarAudience />
      <InlineCTABanner location="inline-1" />
      <WebinarAppShowcase />
      <WebinarTestimonials />
      <WebinarAbout />
      <InlineCTABanner location="inline-2" headline="Klingt nach deinem nächsten Schritt?" />
      <WebinarObjections />
      <WebinarFinalCTA />
      <WebinarFooter />
      <WebinarFormModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <CookieBanner />
    </>
  );
}
